import React, { useEffect, useState } from 'react';
import { Modal, ConfirmModal } from './ui/Modal';
import { useTabsStore, setDbScriptSaveRequest, setDbManagerTabs } from '../store/tabsStore';
import { dbScriptMetaForTab, currentScriptScope, DB_MANAGER_EXTENSION_ID } from '../extensions/dbEngines';
import { invokeAsExtension } from '../extensions/capabilities/enforce';

/**
 * Names an unnamed query tab's script on first Ctrl+S. Saves to
 * scripts/db/<name>.sql (Rust db_script_write), then patches the tab:
 * scriptName + label + savedSql baseline + clears the unsaved dot. Mounted
 * once in App.js; later Ctrl+S saves on named tabs skip this modal entirely.
 */
export default function DbScriptSaveModal() {
  const request = useTabsStore((s) => s.dbScriptSaveRequest);
  const [name, setName] = useState('');
  const [linked, setLinked] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Set when the name is already taken, to confirm the overwrite in-app.
  // window.confirm cannot be used: it is a Tauri dialog-plugin command this
  // app's capabilities do not grant, so it throws instead of returning false.
  const [overwriteName, setOverwriteName] = useState(null);

  useEffect(() => {
    if (request) {
      setName('');
      setLinked(true);
      setError(null);
      setSaving(false);
      setOverwriteName(null);
    }
  }, [request]);

  if (!request) return null;

  const tab = useTabsStore.getState().dbManagerTabs.find((t) => t.id === request.tabId);
  const close = () => setDbScriptSaveRequest(null);

  const save = async (overwriteConfirmed = false) => {
    const trimmed = name.trim();
    if (!trimmed || !tab || saving) return;
    if (/[/\\]/.test(trimmed)) {
      setError('The name cannot contain slashes.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (!overwriteConfirmed) {
        const existing = await invokeAsExtension('db_scripts_list', { extensionId: DB_MANAGER_EXTENSION_ID, scope: currentScriptScope() }).catch(() => []);
        if (existing.some((s) => s.name === trimmed)) {
          setSaving(false);
          setOverwriteName(trimmed);
          return;
        }
      }
      const content = tab.sql || '';
      // Linked scripts get a sidecar binding them to this database (the
      // picker reopens them against it directly); unlinked ones save as
      // plain reusable SQL and the picker asks for a target every time.
      const meta = linked ? dbScriptMetaForTab(tab) : null;
      await invokeAsExtension('db_script_write', { extensionId: DB_MANAGER_EXTENSION_ID, name: trimmed, content, meta, scope: currentScriptScope() });
      setDbManagerTabs((prev) =>
        prev.map((t) =>
          t.id === tab.id
            ? {
                ...t,
                scriptName: trimmed,
                scriptLinked: linked,
                label: t.dbName ? `${trimmed} - "${t.dbName}"` : trimmed,
                savedSql: content,
                hasUnsavedChanges: false,
              }
            : t
        )
      );
      close();
    } catch (e) {
      setError(e?.message || String(e));
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={close} title="Save script" subtitle="Stored under scripts/db as <name>.sql">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
        className="space-y-4"
      >
        <div>
          <label className="modal-logo-label" style={{ display: 'block', marginBottom: '0.375rem' }}>
            Script name *
          </label>
          <input
            type="text"
            autoFocus
            className="modal-input"
            placeholder="e.g. daily-active-users"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {error && <div className="dbq-script-save-error">{error}</div>}
        </div>
        <label className="ext-checkbox-label">
          <input
            type="checkbox"
            className="checkbox-brand"
            checked={linked}
            onChange={(e) => setLinked(e.target.checked)}
          />
          Link script to the current database{tab?.dbName ? ` ("${tab.dbName}")` : ''}
        </label>
        <div className="modal-footer-row">
          <button
            type="button"
            onClick={close}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!name.trim() || saving}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
      {overwriteName && (
        <ConfirmModal
          isOpen
          onClose={() => setOverwriteName(null)}
          onConfirm={() => save(true)}
          title="Overwrite script"
          message={`A script named "${overwriteName}" already exists. Overwrite it?`}
          confirmText="Overwrite"
          cancelText="Cancel"
          type="warning"
        />
      )}
    </Modal>
  );
}
